<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePremiumStore } from '@state/stores/premium'
import { centsToDollars } from '../../../utils/format-price'
import formatDate from './../../../utils/format-date-full'

const premiumStore = usePremiumStore()
const router = useRouter()

const crumbs = [
    { label: 'Account Management', to: '/account' },
    { label: 'Manage subscription' },
]

const currentSubscription = computed(() => premiumStore.currentSubscription)
const paymentMethod = computed(() => premiumStore.paymentMethod)
const isLoading = computed(() => premiumStore.isLoading)
const hasActivePlan = computed(
    () => currentSubscription.value && new Date(currentSubscription.value.endTime) > new Date()
)
const currentPlan = computed(() => (hasActivePlan.value ? `Premium` : 'FRISKY Free'))

const paymentDetails = computed(() => {
    if (!hasActivePlan.value) return ''

    if (paymentMethod.value === 'apple') {
        return 'Your subscription is managed through Apple. To manage your subscription, go to iOS Settings > Apple ID > Subscriptions'
    }

    if (paymentMethod.value === 'google') {
        return 'Your subscription is managed through Google Play. To manage your subscription, go to Google Play Store > Account > Subscriptions'
    }

    if (paymentMethod.value === 'manual') {
        return `Enjoy free Premium subscription on us! It will expire on ${formatDate(new Date(currentSubscription.value.endTime))}`
    }

    if (currentSubscription.value?.cancelTime) {
        return `Your subscription is canceled. It will expire on ${formatDate(new Date(currentSubscription.value.endTime))}`
    }

    return `Your next bill is for ${ centsToDollars(currentSubscription.value.plan.price) } on ${ formatDate(new Date(currentSubscription.value.endTime)) }`
})

const onCancelRequest = () => {
    router.push({ name: 'account/cancel-premium' })
}

usePageHead({
    title: 'Account - Subscription management',
    description: 'Account - Subscription management',
})
</script>

<template>
    <div>
        <BaseBreadcrumbs :crumbs="crumbs" />
        <div
            v-if="isLoading"
            class="cards"
        >
            <BaseCard
                theme="gradient-highlight"
                class="skeleton card"
            />
            <BaseCard class="skeleton card"/>
        </div>
        <div
            v-else
            class="cards"
        >
            <BaseCard
                class="card plan-card"
                theme="gradient-highlight"
            >
                <h2 class="plan-header">Your plan</h2>
                <h3 class="plan-title">{{ currentPlan }}</h3>
                <ul
                    v-if="currentPlan === 'Premium'"
                    class="plan-details-list fw-medium"
                >
                    <li>No ads anywhere</li>
                    <li>High resolution audio streaming</li>
                    <li>Download mixes on mobile and listen offline</li>
                    <li>Streaming in external players and devices</li>
                    <li>Premium-exclusive mixes</li>
                    <li>Customer support by humans</li>
                </ul>
                <br>
            </BaseCard>
            <BaseCard class="card next-payment-card">
                <h2 class="payment-header text-faded">{{ currentSubscription?.cancelTime ? 'Details' : 'Payment' }}</h2>
                <p class="payment-details text-faded">{{ paymentDetails }}</p>
            </BaseCard>
        </div>
        <div
            v-if="!isLoading"
            class="actions"
        >
            <BaseButton
                v-if="!currentSubscription.cancelTime"
                class="cancel-subscription-button"
                variant="secondary"
                size="l"
                type="button"
                @click="onCancelRequest"
            >Cancel Subscription</BaseButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.cards {
    margin-top: $s-xl;
}

.card {
    min-height: 230px;
}

.plan-header {
    @extend %contentTitleM;
}

.plan-title {
    @extend %h4;

    margin: $s-m 0;
    line-height: auto;
}

.plan-details-list {
    padding-left: $s-l;
    font-size: 14px;
    line-height: 18px;
    color: $c-white;
}

.payment-header {
    @extend %h5;
}

.actions {
    display: flex;
    gap: $s-xl;
    align-items: center;
    justify-content: center;
    margin-top: $s-xxl;
}

@include tablet {
    .actions {
        justify-content: flex-end;
    }
}
</style>
